import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

interface AuthBottomSheetProps {
  onClose: () => void;
  onAuthenticated: () => void;
}

const AuthBottomSheet = ({ onClose, onAuthenticated }: AuthBottomSheetProps) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (isLogin) {
      const { error } = await signIn(email, password);
      if (error) {
        toast({ variant: "destructive", title: t("auth.signInFailed"), description: error.message });
      } else {
        onAuthenticated();
      }
    } else {
      const { error } = await signUp(email, password, displayName);
      if (error) {
        toast({ variant: "destructive", title: t("auth.signUpFailed"), description: error.message });
      } else {
        toast({ title: t("auth.checkEmail"), description: t("auth.verificationSent") });
      }
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-bold text-foreground font-serif">
            {isLogin ? t("auth.welcomeBack") : t("auth.createAccount")}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5">
          <p className="text-sm text-muted-foreground mb-5">
            {isLogin ? t("auth.signInManage") : t("auth.startCreating")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="bs-name" className="text-xs text-muted-foreground">{t("auth.yourName")}</Label>
                <Input
                  id="bs-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Jane Doe"
                  required={!isLogin}
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label htmlFor="bs-email" className="text-xs text-muted-foreground">{t("auth.email")}</Label>
              <Input
                id="bs-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="bs-password" className="text-xs text-muted-foreground">{t("auth.password")}</Label>
              <Input
                id="bs-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="mt-1"
              />
            </div>

            <Button type="submit" className="w-full rounded-full" size="lg" disabled={loading}>
              {loading ? t("auth.loading") : isLogin ? t("auth.signIn") : t("auth.signUp")}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-muted-foreground">
            {isLogin ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary font-semibold hover:underline"
            >
              {isLogin ? t("auth.signUpLink") : t("auth.signInLink")}
            </button>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AuthBottomSheet;
